import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Bot, User, ToggleLeft, ToggleRight, Phone, MapPin, Mic, Video, FileText, Download, MousePointerClick } from 'lucide-react';
import { socket } from '../chat/socket';

const API_URL = process.env.REACT_APP_API_URL;

function SalesChatView({ chat, userId, onChatUpdate }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const limit = 50;

  // Fetch messages when chat changes
  useEffect(() => {
    if (chat) {
      fetchMessages(true);
      markAsRead();
      
      // Join this specific chat room
      socket.emit('admin_join_sales_chat', { chat_id: chat.id });
      const handleConnect = () => socket.emit('admin_join_sales_chat', { chat_id: chat.id });
      socket.on('connect', handleConnect);

      return () => {
        socket.off('connect', handleConnect);
        socket.emit('admin_leave_sales_chat', { chat_id: chat.id });
      };
    }
  }, [chat?.id]);

  // Listen for new messages via Socket.IO
  useEffect(() => {
    const handleNewMessage = (messageData) => {
      if (messageData.chat_id === chat.id) {
        setMessages(prev => [...prev, messageData]);
        scrollToBottom();
        markAsRead();
      }
    };

    socket.on('new_sales_message', handleNewMessage);

    return () => {
      socket.off('new_sales_message', handleNewMessage);
    };
  }, [chat?.id]);

  const fetchMessages = async (isInitial = false, beforeId = null) => {
    if (isInitial) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const params = new URLSearchParams({
        limit: limit
      });
      if (beforeId) {
        params.append('before_id', beforeId);
      }

      const response = await fetch(`${API_URL}/api/sales/chats/${chat.id}/messages?${params}`);
      const data = await response.json();

      if (isInitial) {
        setMessages(data.messages);
        setTimeout(scrollToBottom, 100);
      } else {
        // Prepend older messages
        const container = messagesContainerRef.current;
        const previousScrollHeight = container.scrollHeight;
        
        setMessages(prev => [...data.messages, ...prev]);
        
        // Maintain scroll position
        setTimeout(() => {
          container.scrollTop = container.scrollHeight - previousScrollHeight;
        }, 0);
      }

      setHasMoreMessages(data.has_more);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const markAsRead = async () => {
    try {
      await fetch(`${API_URL}/api/sales/chats/${chat.id}/mark-read`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const response = await socket.emitWithAck('admin_send_sales_message', {
        chat_id: chat.id,
        message: newMessage,
        admin_user_id: userId
      });

      if (response.ok) {
        setNewMessage('');
        scrollToBottom();
      } else {
        alert('Error al enviar mensaje: ' + response.error);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error al enviar mensaje');
    } finally {
      setSending(false);
    }
  };

  const toggleBot = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sales/chats/${chat.id}/toggle-bot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          is_bot_active: !chat.is_bot_active,
          admin_user_id: userId
        })
      });

      const data = await response.json();
      if (data.success) {
        onChatUpdate(data.chat);
      }
    } catch (error) {
      console.error('Error toggling bot:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Infinite scroll - load older messages when scrolling up
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current || loadingMore || !hasMoreMessages) return;

    const { scrollTop } = messagesContainerRef.current;
    if (scrollTop < 100) {
      const oldestMessage = messages[0];
      if (oldestMessage) {
        fetchMessages(false, oldestMessage.id);
      }
    }
  }, [messages, loadingMore, hasMoreMessages]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Hoy';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ayer';
    } else {
      return date.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' });
    }
  };

  const renderMessage = (message, index) => {
    const isInbound = message.direction === 'inbound';
    const showDate = index === 0 || 
      formatDate(messages[index - 1]?.timestamp) !== formatDate(message.timestamp);

    return (
      <React.Fragment key={message.id}>
        {/* Date separator */}
        {showDate && (
          <div className="flex justify-center my-4">
            <span className="px-4 py-1 bg-gray-200 text-gray-600 text-xs rounded-full">
              {formatDate(message.timestamp)}
            </span>
          </div>
        )}

        {/* Message bubble */}
        <div className={`flex ${isInbound ? 'justify-start' : 'justify-end'} mb-2`}>
          <div className={`max-w-[70%] ${isInbound ? 'order-2' : 'order-1'}`}>
            {/* Message content */}
            <div
              className={`px-4 py-2 rounded-2xl ${
                isInbound
                  ? 'bg-white border border-gray-200 text-gray-900'
                  : message.sent_by_agent_id
                  ? 'bg-purple-600 text-white'
                  : 'bg-[#B1336E] text-white'
              }`}
            >
              {/* Media content */}
              {message.media_type === 'image' && message.media_url && (
                <img
                  src={message.media_url}
                  alt="Imagen"
                  className="rounded-lg mb-2 max-w-full cursor-pointer hover:opacity-90"
                  onClick={() => window.open(message.media_url, '_blank')}
                />
              )}

              {message.media_type === 'video' && message.media_url && (
                <div className="mb-2">
                  <div className="mb-1 flex items-center gap-2 text-sm font-medium">
                    <Video className="w-4 h-4" />
                    Video
                  </div>
                  <video controls preload="metadata" src={message.media_url} className="max-h-80 w-full rounded-lg bg-black" />
                </div>
              )}

              {message.media_type === 'document' && message.media_url && (
                <a
                  href={message.media_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mb-2 flex items-center gap-3 rounded-lg bg-black bg-opacity-10 p-3"
                >
                  <FileText className="w-5 h-5" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{message.content || 'Documento adjunto'}</p>
                    <p className="text-xs opacity-75">Abrir archivo</p>
                  </div>
                  <Download className="w-4 h-4" />
                </a>
              )}

              {['sticker', 'interactive', 'button', 'reaction', 'order', 'referral', 'system', 'unsupported'].includes(message.media_type) && (
                <div className="mb-2 flex items-start gap-2 rounded-lg bg-black bg-opacity-10 p-2">
                  <MousePointerClick className="mt-0.5 w-4 h-4" />
                  <div className="text-sm">
                    <p className="font-medium">{message.media_type === 'unsupported' ? 'Tipo WhatsApp no soportado' : 'Mensaje WhatsApp'}</p>
                    <p className="text-xs opacity-80">{message.content || message.media_type}</p>
                  </div>
                </div>
              )}

              {message.media_type === 'location' && (
                <div className="flex items-start space-x-2 mb-2 p-2 bg-black bg-opacity-10 rounded">
                  <MapPin className="w-4 h-4 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium">Ubicación compartida</p>
                    {message.latitude && message.longitude && (
                        <a
                        href={`https://www.google.com/maps?q=${message.latitude},${message.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs underline"
                      >
                        Ver en Google Maps
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* ========== NEW: Audio message display ========== */}
              {message.media_type === 'audio' && (
                <div className="flex items-start space-x-2 mb-2 p-2 bg-black bg-opacity-10 rounded">
                  <Mic className="w-4 h-4 mt-0.5" />
                  <div className="text-sm flex-1">
                    <p className="font-medium">Mensaje de audio</p>
                    {message.media_url && (
                      <audio controls className="mt-1 w-full">
                        <source src={message.media_url} type="audio/ogg" />
                        <source src={message.media_url} type="audio/mpeg" />
                        Tu navegador no soporta audio.
                      </audio>
                    )}
                    {message.content && 
                     !message.content.includes('[Audio - transcription failed]') &&
                     !message.content.includes('[Audio - processing error]') && (
                      <div className="mt-2 pt-2 border-t border-white border-opacity-20">
                        <p className="text-xs opacity-75">Transcripción:</p>
                        <p className="text-xs mt-1">{message.content}</p>
                      </div>
                    )}
                    {(message.content?.includes('[Audio - transcription failed]') ||
                      message.content?.includes('[Audio - processing error]')) && (
                      <p className="text-xs mt-1 opacity-75">
                        ⚠️ No se pudo transcribir el audio
                      </p>
                    )}
                  </div>
                </div>
              )}
              {/* ========== END AUDIO DISPLAY ========== */}

              {/* Text content (only if not audio or audio doesn't have special markers) */}
              {!['audio', 'document', 'interactive', 'button', 'reaction', 'order', 'referral', 'system', 'unsupported'].includes(message.media_type) && (
                <p className="text-sm whitespace-pre-wrap break-words">
                  {message.content}
                </p>
              )}

              {/* Timestamp and status */}
              <div className={`flex items-center justify-end space-x-1 mt-1 text-xs ${
                isInbound ? 'text-gray-500' : 'text-white text-opacity-70'
              }`}>
                <span>{formatTime(message.timestamp)}</span>
                {!isInbound && message.processed_by_ai && (
                  <Bot className="w-3 h-3" />
                )}
                {!isInbound && message.sent_by_agent_id && (
                  <User className="w-3 h-3" />
                )}
              </div>
            </div>

            {/* AI intent tag (for debugging) */}
            {message.ai_intent_detected && (
              <p className="text-xs text-gray-400 mt-1 ml-2">
                Intent: {message.ai_intent_detected}
                {message.ai_confidence && ` (${(message.ai_confidence * 100).toFixed(0)}%)`}
              </p>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B1336E] mx-auto mb-4"></div>
          <p className="text-sm text-gray-500">Cargando mensajes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Chat Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
              {chat.customer_name?.[0]?.toUpperCase() || '?'}
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">
                {chat.customer_name || 'Cliente'}
              </h2>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Phone className="w-3 h-3" />
                <span>{chat.phone_number}</span>
              </div>
            </div>
          </div>

          {/* Bot Toggle */}
          <button
            onClick={toggleBot}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              chat.is_bot_active
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
            }`}
          >
            {chat.is_bot_active ? (
              <>
                <Bot className="w-5 h-5" />
                <span>Bot Activo</span>
                <ToggleRight className="w-5 h-5" />
              </>
            ) : (
              <>
                <User className="w-5 h-5" />
                <span>Modo Manual</span>
                <ToggleLeft className="w-5 h-5" />
              </>
            )}
          </button>
        </div>

        {/* Stage indicator */}
        <div className="mt-3 flex items-center space-x-2">
          <span className="text-xs text-gray-500">Estado:</span>
          <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
            {chat.conversation_stage}
          </span>
          {chat.current_intent && (
            <>
              <span className="text-xs text-gray-500">•</span>
              <span className="text-xs text-gray-600">
                Intent: {chat.current_intent}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Messages Container */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-6 py-4 bg-gray-50"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d1d5db' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      >
        {loadingMore && (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#B1336E] mx-auto"></div>
          </div>
        )}

        {messages.map((message, index) => renderMessage(message, index))}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="bg-white border-t border-gray-200 px-6 py-4">
        {!chat.is_bot_active ? (
          <form onSubmit={handleSendMessage} className="flex items-center space-x-3">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Escribe un mensaje..."
              disabled={sending}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
            />
            <button
              type="submit"
              disabled={sending || !newMessage.trim()}
              className="p-3 bg-[#B1336E] text-white rounded-full hover:bg-[#8f2858] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </form>
        ) : (
          <div className="text-center py-2">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-green-50 text-green-700 rounded-full">
              <Bot className="w-4 h-4" />
              <span className="text-sm font-medium">
                El bot está respondiendo automáticamente
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SalesChatView;
